// Reportes de la propia agenda del médico (Rediseño mayor 2026-09-02,
// Fase 5 "destinos reales del nav") — rango de fechas elegible (7/30/90
// días + custom) sobre GET /api/hce/reportes, exportable a CSV.
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TopNav } from '@/dashboard/TopNav';
import { descargarReportesCsv, obtenerReportes, type ReportesHce } from '@/lib/hceVault';

type RangoPreset = '7' | '30' | '90' | 'custom';

function fechaISO(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function rangoDePreset(preset: RangoPreset, desdeCustom: string, hastaCustom: string): { desde: Date; hasta: Date } {
  const hasta = preset === 'custom' && hastaCustom ? new Date(hastaCustom) : new Date();
  if (preset === 'custom') {
    return { desde: desdeCustom ? new Date(desdeCustom) : new Date(hasta.getTime() - 30 * 24 * 60 * 60 * 1000), hasta };
  }
  const dias = Number(preset);
  return { desde: new Date(hasta.getTime() - dias * 24 * 60 * 60 * 1000), hasta };
}

export function ReportesPage(): JSX.Element {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<RangoPreset>('30');
  const [desdeCustom, setDesdeCustom] = useState('');
  const [hastaCustom, setHastaCustom] = useState('');
  const [reportes, setReportes] = useState<ReportesHce | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportando, setExportando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const { desde, hasta } = rangoDePreset(preset, desdeCustom, hastaCustom);
      setReportes(await obtenerReportes(desde, hasta));
    } catch {
      setError(t('reportes.errorCargar'));
    }
    setCargando(false);
  }, [preset, desdeCustom, hastaCustom, t]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function exportar(): Promise<void> {
    setExportando(true);
    try {
      const { desde, hasta } = rangoDePreset(preset, desdeCustom, hastaCustom);
      await descargarReportesCsv(desde, hasta);
    } catch {
      setError(t('reportes.errorExportar'));
    }
    setExportando(false);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4 py-4">
      <TopNav activo="reportes" />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-medium text-hce-ink">{t('reportes.titulo')}</h1>
        <Button size="sm" variant="outline" disabled={exportando} onClick={() => void exportar()}>
          {exportando ? t('reportes.exportando') : t('reportes.exportarCsv')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs font-mono uppercase tracking-wide">
        {(['7', '30', '90', 'custom'] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPreset(p)}
            className={preset === p ? 'border-b-2 border-hce-pine pb-0.5 text-hce-pine' : 'pb-0.5 text-muted-foreground'}
          >
            {p === 'custom' ? t('reportes.rango.custom') : t('reportes.rango.dias', { count: Number(p) })}
          </button>
        ))}
      </div>
      {preset === 'custom' && (
        <div className="flex items-center gap-2">
          <Input type="date" value={desdeCustom} onChange={(e) => setDesdeCustom(e.target.value)} max={hastaCustom || fechaISO(new Date())} />
          <span className="text-hce-muted">—</span>
          <Input type="date" value={hastaCustom} onChange={(e) => setHastaCustom(e.target.value)} max={fechaISO(new Date())} />
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
      {reportes?.sinEspecialidadVinculada && <p className="text-sm text-hce-muted">{t('dashboard.sinVinculo')}</p>}

      {cargando ? (
        <p className="text-sm text-hce-muted">{t('dashboard.cargando')}</p>
      ) : reportes ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <MetricaTile label={t('dashboard.kpi.total')} valor={reportes.totalCitas} />
          <MetricaTile label={t('dashboard.kpi.nuevos')} valor={reportes.pacientesNuevos} />
          <MetricaTile label={t('dashboard.kpi.recurrentes')} valor={reportes.pacientesRecurrentes} />
          <MetricaTile label={t('reportes.noShow')} valor={reportes.noShowCount} />
          <MetricaTile
            label={t('reportes.tasaNoShow')}
            valor={reportes.noShowRate === null ? '—' : `${(reportes.noShowRate * 100).toFixed(1)}%`}
          />
        </div>
      ) : null}
    </div>
  );
}

function MetricaTile({ label, valor }: { label: string; valor: number | string }): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-hce-paper p-3">
      <div className="text-[11px] font-mono uppercase tracking-wide text-hce-muted">{label}</div>
      <div className="mt-1 text-2xl font-medium text-hce-ink">{valor}</div>
    </div>
  );
}
